import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FaSpinner, FaUpload, FaTimes } from "react-icons/fa";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  displayName: string;
  avatarUrl?: string | null;
  size?: "md" | "lg";
  editable?: boolean;
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function UserAvatar({
  displayName,
  avatarUrl,
  size = "lg",
  editable = false,
}: UserAvatarProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, updateProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [localAvatarUrl, setLocalAvatarUrl] = useState(avatarUrl);

  useEffect(() => {
    setLocalAvatarUrl(avatarUrl);
  }, [avatarUrl]);

  const sizeClass = size === "lg" ? "h-16 w-16" : "h-12 w-12";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: t("profile.avatarInvalidType"),
        description: t("profile.avatarInvalidTypeDesc"),
        variant: "destructive",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("profile.avatarTooLarge"),
        description: t("profile.avatarTooLargeDesc"),
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleClearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setImageFile(null);
    setPreviewUrl(null);
  };

  const handleUpload = async () => {
    if (!imageFile || !user?.id) return;

    setIsUploading(true);
    try {
      const filePath = `user-avatars/${user.id}_${Date.now()}`;
      const { data, error } = await supabase.storage
        .from("avatars")
        .upload(filePath, imageFile, {
          cacheControl: "3600",
          upsert: true,
          contentType: imageFile.type,
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(data.path);
      await updateProfile({ avatar_url: urlData.publicUrl });
      setLocalAvatarUrl(urlData.publicUrl);

      toast({
        title: t("common.success"),
        description: t("profile.avatarUpdated"),
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast({
        title: t("errors.error"),
        description: t("profile.avatarUploadError"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      handleClearFile();
    }
  };

  const shownUrl = localAvatarUrl ?? avatarUrl;

  return (
    <div className="relative inline-flex">
      <Avatar className={sizeClass}>
        <AvatarImage src={shownUrl || undefined} alt={displayName} />
        <AvatarFallback className="bg-primary/10">{getInitials(displayName)}</AvatarFallback>
      </Avatar>

      {editable && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "absolute bottom-0 right-0 h-6 w-6 rounded-full bg-background shadow-sm p-0",
                "border border-border"
              )}
            >
              <FaUpload className="h-3 w-3" />
              <span className="sr-only">{t("profile.changeAvatar")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("profile.changeAvatar")}</DialogTitle>
              <DialogDescription>{t("profile.changeAvatarDesc")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex justify-center">
                <Avatar className="h-24 w-24">
                  {previewUrl ? (
                    <AvatarImage src={previewUrl} alt="Preview" />
                  ) : (
                    <>
                      <AvatarImage src={shownUrl || undefined} alt={displayName} />
                      <AvatarFallback className="bg-primary/10 text-xl">
                        {getInitials(displayName)}
                      </AvatarFallback>
                    </>
                  )}
                </Avatar>
              </div>

              {previewUrl ? (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={handleClearFile} disabled={isUploading}>
                    <FaTimes className="h-4 w-4 mr-2" />
                    {t("common.cancel")}
                  </Button>
                </div>
              ) : (
                <div className="grid w-full max-w-sm items-center gap-1.5 mx-auto">
                  <Label htmlFor="user-avatar">{t("profile.selectImage")}</Label>
                  <Input
                    id="user-avatar"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  <p className="text-xs text-muted-foreground">{t("profile.avatarFileHint")}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isUploading}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleUpload} disabled={!imageFile || isUploading}>
                {isUploading && <FaSpinner className="mr-2 h-4 w-4 animate-spin" />}
                {isUploading ? t("common.loading") : t("profile.uploadAvatar")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
